import { useEffect, useMemo, useState } from 'react';

import { storage, storageKeys } from '@/constants/storage';
import type { LDIdentityState } from '@/types/launchDarkly';
import type { GuestIdentity } from '@/utils/guestLdIdentity';
import {
  COLLAPSED_GUEST_IDENTITY,
  isRandomizationUnitKey,
  resolveGuestIdentity,
} from '@/utils/guestLdIdentity';
import { Logger } from '@/utils/logging';
import { Tracker } from '@/utils/tracking';

import type { LdIdentityAdapter } from './types';

const LD_NETWORK_ERRORS = new Set([
  'LaunchDarklyFlagFetchError',
  'LaunchDarklyTimeoutError',
]);

/**
 * Resolves the guest LaunchDarkly identity on mount and exposes the state both
 * providers put on `LaunchDarklyIdentityContext`. Every SDK call goes through
 * `adapter`, so web and native run the same sequence and cannot drift.
 */
export const useGuestLdIdentity = (
  adapter: LdIdentityAdapter | null,
): LDIdentityState => {
  const [isIdentified, setIsIdentified] = useState(false);
  const [guestIdentity, setGuestIdentity] = useState<GuestIdentity>(
    COLLAPSED_GUEST_IDENTITY,
  );

  useEffect(() => {
    if (!adapter) return;

    const resolve = async () => {
      try {
        const identifyGuests = await adapter.readIdentifyGuestsFlag();

        // Tracker.initialize() is what writes the anonymous ID, so reading it
        // early collapses a first-install guest for the whole session.
        await Tracker.ready;

        const identity = resolveGuestIdentity({
          identifyGuests,
          anonymousId: storage.getString(storageKeys.rudderstackAnonymusUserId),
        });

        // Kept for the logout path, which has to revert to the identity the
        // flag allowed rather than the anonymous ID directly.
        setGuestIdentity(identity);

        // A customer signing in mid-flight is already a valid randomization
        // unit; identifying a guest over it would demote them.
        if (isRandomizationUnitKey(adapter.currentUserKey())) {
          setIsIdentified(true);
          Logger.console(
            '[LaunchDarkly] Guest identify skipped: a customer context is already applied',
          );
          return;
        }

        // Skipped only when the client already holds this exact key in the
        // multi-kind shape, which web's single-kind bootstrap never does.
        if (adapter.currentUserKey() !== identity.key) {
          await adapter.identifyGuest(identity);
        }

        setIsIdentified(identity.isIdentified);
      } catch (error) {
        const context =
          error instanceof Error
            ? { message: error.message, name: error.name }
            : { error: String(error) };
        // LaunchDarkly being unreachable (fetch error, timeout) is the network.
        const isUnreachable =
          error instanceof Error && LD_NETWORK_ERRORS.has(error.name);
        (isUnreachable ? Logger.warn : Logger.error)(
          '[LaunchDarkly] Guest identity resolution failed',
          context,
        );
      }
    };

    resolve();
  }, [adapter]);

  return useMemo<LDIdentityState>(
    () => ({ isIdentified, guestIdentity, setIsIdentified }),
    [isIdentified, guestIdentity],
  );
};
